import { useState } from 'react';
import { Table, Tag, Button, Card, Pagination } from 'antd';
import { ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import { paiseToRupees, formatDate } from '../../utils/format';
import ErrorAlert from '../../components/ErrorAlert';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { notifyError } from '../../utils/notify';

const emptyState = (
  <EmptyState
    icon={<FileTextOutlined />}
    title="No invoices yet"
    description="Invoices are issued automatically after each successful payment — your initial subscription and any user add-ons will appear here."
  />
);

// Per-row action — text-only "View" link button. New actions
// (download PDF, resend email, etc.) drop into the same flex
// container without changing the row layout.
function ReportButton({ invoiceId, navigate }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      const out = await endpoints.reports.createJob({
        reportCode: 'subscription_invoice',
        format: 'html',
        inputParams: { invoiceId }
      });
      const jobId = out && out.job && out.job.id;
      if (jobId) navigate(`/reports/viewer/${jobId}`);
    } catch (err) {
      notifyError(err, 'Could not queue this invoice in the report engine.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      size="small"
      type="link"
      loading={busy}
      onClick={run}
      aria-label="View report"
      className="!px-2 !h-8 !font-semibold !text-brand-600 hover:!text-brand-700"
    >
      View
    </Button>
  );
}

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { data, error, loading, refetch } = useQuery(
    () => endpoints.invoices.list({ page, pageSize }),
    [page, pageSize]
  );

  const items = (data && data.items) || [];
  const total = (data && data.total) || 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      width: 150,
      render: (v, row) => (
        <Link
          to={`/invoices/${row.id}`}
          className="text-brand-600 hover:text-brand-700 font-semibold font-mono text-[12.5px]"
        >
          {v}
        </Link>
      )
    },
    {
      title: 'Date',
      dataIndex: 'invoiceDate',
      width: 110,
      render: (v) => (
        <span className="text-ink-700 whitespace-nowrap">{formatDate(v)}</span>
      )
    },
    {
      title: 'Cycle',
      dataIndex: 'billingCycle',
      width: 100,
      render: (v) => (
        <Tag color="blue" className="!m-0 capitalize">
          {v}
        </Tag>
      )
    },
    {
      title: 'Plan',
      dataIndex: 'planCode',
      width: 140,
      render: (v, row) => (
        <span className="text-ink-700">
          <span className="font-medium text-ink-900">{v}</span>
          <span className="text-ink-400 ml-1">v{row.planVersion ?? '—'}</span>
        </span>
      )
    },
    {
      title: 'Place of supply',
      dataIndex: 'placeOfSupplyState',
      ellipsis: true,
      render: (v) => <span className="text-ink-700">{v || '—'}</span>
    },
    {
      title: 'Total',
      dataIndex: 'totalAmountPaise',
      align: 'right',
      width: 130,
      render: (v) => (
        <span className="font-semibold text-ink-900 tabular-nums whitespace-nowrap">
          {paiseToRupees(v)}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'report',
      align: 'right',
      width: 100,
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-1">
          <ReportButton invoiceId={row.id} navigate={navigate} />
        </div>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="All invoices issued to your account — initial subscription, user add-ons, and renewals."
        icon={<FileTextOutlined />}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      <ErrorAlert error={error} />

      {/* Cap the table card on very wide monitors so the 7-column
          invoice grid keeps a comfortable line length. Below the
          cap the card expands naturally — fully responsive. */}
      <div className="max-w-6xl">
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="small"
            dataSource={items}
            columns={columns}
            loading={loading}
            locale={{ emptyText: emptyState }}
            rowClassName={() => 'invoice-row'}
            className="invoice-list-table"
            pagination={false}
          />

          {/* Custom split pagination footer — total range on the
              left, numbered Pagination control on the right. Mirrors
              the modern data-table reference (Stripe / Linear /
              customer-list inspiration). Only renders when there is
              data to page through. */}
          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-surface-200">
              <span className="text-sm text-ink-500">
                Showing{' '}
                <span className="font-semibold text-ink-700">
                  {rangeStart}–{rangeEnd}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-ink-700">
                  {total.toLocaleString()}
                </span>{' '}
                invoices
              </span>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                size="small"
                onChange={(p, s) => {
                  setPage(p);
                  setPageSize(s);
                }}
              />
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
